import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import moment from "moment";
import "moment/locale/ar";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { ContractContext } from "../../../../providers/contract";
import {
  EShipmentType,
  ESenderRole,
  IAddressDTO,
} from "../../../../setup/api/services/contract/models";
import { IShipmentTrackingEvent } from "../../../../setup/api/services/shipping/models";
import { trackShipment } from "../../../../setup/api/services/shipping";
import styles from "./styles.module.scss";

const formatTimestamp = (ts: string) =>
  moment(ts).locale("ar").format("DD MMMM YYYY - hh:mm A");

/** Normalize an activity/status string to a translation key.
 *  Handles: "Out for Delivery", "OUT_FOR_DELIVERY", "OutForDelivery" */
const toActivityKey = (activity: string) =>
  `shipment_activity_${activity
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1_$2") // camelCase → snake_case
    .toLowerCase()
    .replace(/[\s-]+/g, "_")}`;

const AddressCard = ({
  address,
  submittedAt,
}: {
  address: IAddressDTO;
  submittedAt?: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.address_card}>
      {address.street && (
        <p className={styles.address_main}>{address.street}</p>
      )}
      {address.nationalAddress && (
        <p className={styles.address_row}>
          <span className={styles.address_lbl}>
            {t("lbl_national_address")} :
          </span>
          <span className={styles.address_val}>{address.nationalAddress}</span>
        </p>
      )}
      {address.district && (
        <p className={styles.address_row}>
          <span className={styles.address_lbl}>{t("lbl_district")} :</span>
          <span className={styles.address_val}>{address.district}</span>
        </p>
      )}
      {address.city && (
        <p className={styles.address_row}>
          <span className={styles.address_lbl}>{t("lbl_city")} :</span>
          <span className={styles.address_val}>{address.city}</span>
        </p>
      )}
      {address.postalCode && (
        <p className={styles.address_row}>
          <span className={styles.address_lbl}>{t("lbl_postal_code")} :</span>
          <span className={styles.address_val}>{address.postalCode}</span>
        </p>
      )}
      {submittedAt && (
        <div className={styles.timestamp_row}>
          <AccessTimeIcon fontSize="small" className={styles.timestamp_icon} />
          <span className={styles.timestamp_text}>
            {t("lbl_submitted_at")}: {formatTimestamp(submittedAt)}
          </span>
        </div>
      )}
    </div>
  );
};

const ShippingDetails = () => {
  const { contract } = useContext(ContractContext);
  const { t } = useTranslation();
  const [copiedBooking, setCopiedBooking] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [trackingEvents, setTrackingEvents] = useState<
    IShipmentTrackingEvent[]
  >([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const isNaqelShipment =
    contract?.shippable !== false &&
    (contract?.shippable === true ||
      contract?.shipment?.type === EShipmentType.SHIPMENT_COMPANY);

  const fetchTrackingEvents = useCallback(async () => {
    if (!contract?.shortId || !isNaqelShipment) return;
    setLoadingEvents(true);
    try {
      const response = await trackShipment(contract.shortId);
      if (response?.data) {
        setTrackingEvents(response.data);
      }
    } catch {
      // silently ignore — events table will show empty state
    } finally {
      setLoadingEvents(false);
    }
  }, [contract?.shortId, isNaqelShipment]);

  useEffect(() => {
    fetchTrackingEvents();
  }, [fetchTrackingEvents]);

  if (!isNaqelShipment) return null;

  const shipment = contract?.shipment;

  // Determine seller (pickup) vs buyer (delivery) from senderRole
  const sellerId =
    contract?.senderRole === ESenderRole.PROVIDER
      ? contract?.senderId
      : contract?.receiverId;
  const buyerId =
    contract?.senderRole === ESenderRole.PROVIDER
      ? contract?.receiverId
      : contract?.senderId;

  // addressesMap is keyed by userId
  const pickupAddress: IAddressDTO | undefined = sellerId
    ? contract?.addressesMap?.[sellerId]
    : undefined;

  const deliveryAddress: IAddressDTO | undefined =
    buyerId && contract?.addressesMap?.[buyerId]
      ? contract.addressesMap[buyerId]
      : contract?.address
      ? {
          street: contract.address.street,
          city: contract.address.city,
          district: contract.address.district,
          postalCode: contract.address.postalCode,
        }
      : undefined;

  // Tracking number: bookingId is the Naqel booking ref, fall back to trackingCode
  const trackingNumber = shipment?.shipmentRequestId || shipment?.trackingCode;

  // Waybill URL: stickerUrl from API, fall back to legacy waybillUrl
  // Also check the latest tracking event for an updated stickerFileUrl
  const latestEvent = trackingEvents[trackingEvents.length - 1];
  const waybillUrl =
    latestEvent?.stickerFileUrl ||
    shipment?.stickerUrl ||
    shipment?.waybillUrl ||
    null;

  // Tracking URL: from latest event or shipment field, otherwise build from bookingId
  const trackingUrl =
    latestEvent?.trackingURL ||
    shipment?.trackingURL ||
    (shipment?.bookingId
      ? `https://www.naqel.com.sa/tracking-details/?trackingNumber=${shipment.bookingId}`
      : null);

  const handleTrackShipment = () => {
    if (trackingUrl) window.open(trackingUrl, "_blank");
  };

  const handleDownloadWaybill = () => {
    if (waybillUrl) window.open(waybillUrl, "_blank");
  };

  const handleCopyBooking = () => {
    if (trackingNumber) {
      navigator.clipboard.writeText(trackingNumber);
      setCopiedBooking(true);
      setTimeout(() => setCopiedBooking(false), 2000);
    }
  };

  // arabicActivity starting with "تم" = completed (green)
  const isCompletedEvent = (event: IShipmentTrackingEvent) =>
    event.arabicActivity?.startsWith("تم");

  // Company to display: prefer shipmentCompany ("NAQEL"), skip "OTO" (platform name)
  const companyName =
    shipment?.shipmentCompany ||
    (shipment?.provider !== "OTO" ? shipment?.provider : null) ||
    t("lbl_naqel");

  return (
    <div className={styles.container}>
      <button
        className={styles.header_btn}
        onClick={() => setCollapsed((c) => !c)}>
        <div>
          <h2 className={styles.title}>{t("SHIPPING_DETAILS")}</h2>
          <p className={styles.subtitle}>
            {t("lbl_shipping_details_subtitle")}
          </p>
        </div>
        <KeyboardArrowDownIcon
          className={classNames(styles.chevron, {
            [styles.chevron_collapsed]: collapsed,
          })}
        />
      </button>

      {!collapsed && (
        <>
          {/* Waybill (outlined) first → right in RTL; Track (filled) second → left in RTL */}
          <div className={styles.actions_row}>
            <button
              className={styles.waybill_btn}
              onClick={handleDownloadWaybill}
              disabled={!waybillUrl}>
              {t("lbl_waybill")}
              <FileDownloadOutlinedIcon fontSize="small" />
            </button>
            <button
              className={styles.track_btn}
              onClick={handleTrackShipment}
              disabled={!trackingUrl}>
              {t("lbl_track_shipment")}
              <OpenInNewIcon fontSize="small" />
            </button>
          </div>

          {/* Booking / tracking number */}
          <div className={styles.info_card}>
            <p className={styles.info_label}>{t("lbl_tracking_number")}</p>
            <div className={styles.info_value_row}>
              <span className={styles.tracking_code}>
                {trackingNumber || "-"}
              </span>
              {trackingNumber && (
                <button className={styles.copy_btn} onClick={handleCopyBooking}>
                  {copiedBooking ? (
                    <CheckIcon fontSize="small" className={styles.check_icon} />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Shipping company */}
          <div className={styles.info_card}>
            <p className={styles.info_label}>{t("lbl_shipped_with_waffy")}</p>
            <p className={styles.info_value}>{companyName}</p>
            <p className={styles.info_value_muted}>
              {shipment?.deliveryOptionName ?? ""}
            </p>
          </div>

          {/* Pickup address — seller */}
          <div className={styles.address_section}>
            <div className={styles.section_header}>
              <Inventory2OutlinedIcon className={styles.pickup_icon} />
              <h3 className={styles.section_title}>
                {t("lbl_pickup_address_seller")}
              </h3>
            </div>
            {pickupAddress ? (
              <AddressCard
                address={pickupAddress}
                submittedAt={shipment?.createdAt}
              />
            ) : (
              <div className={styles.empty_address}>
                <LocationOnOutlinedIcon className={styles.empty_icon} />
                <p className={styles.empty_text}>
                  {t("lbl_address_not_specified")}
                </p>
              </div>
            )}
          </div>

          {/* Delivery address — buyer */}
          <div className={styles.address_section}>
            <div className={styles.section_header}>
              <LocationOnOutlinedIcon className={styles.delivery_icon} />
              <h3 className={styles.section_title}>
                {t("lbl_delivery_address_buyer")}
              </h3>
            </div>
            {deliveryAddress ? (
              <AddressCard address={deliveryAddress} />
            ) : (
              <div className={styles.empty_address}>
                <LocationOnOutlinedIcon className={styles.empty_icon} />
                <p className={styles.empty_text}>
                  {t("lbl_address_not_specified")}
                </p>
              </div>
            )}
          </div>

          {/* Events log — fetched from /api/shipping/{contractId}/track */}
          <div className={styles.events_section}>
            <h3 className={styles.events_title}>{t("lbl_events_log")}</h3>
            <div className={styles.events_table}>
              {/* Timestamp RIGHT (first in RTL grid), Status LEFT (second) */}
              <div
                className={classNames(styles.events_row, styles.events_header)}>
                <span>{t("lbl_event_timestamp")}</span>
                <span>{t("lbl_event_status")}</span>
              </div>
              {loadingEvents ? (
                <div className={styles.events_empty}>{t("lbl_loading")}</div>
              ) : trackingEvents.length > 0 ? (
                trackingEvents.map((event, index) => (
                  <div
                    key={event.id ?? index}
                    className={classNames(styles.events_row, {
                      [styles.events_row_alt]: index % 2 !== 0,
                    })}>
                    <span className={styles.event_time}>
                      {formatTimestamp(event.date)}
                    </span>
                    <span
                      className={classNames(styles.event_status, {
                        [styles.event_status_completed]:
                          isCompletedEvent(event),
                      })}>
                      <span className={styles.event_status_ar}>
                        {(() => {
                          if (event.arabicActivity) return event.arabicActivity;
                          for (const raw of [event.activity, event.status]) {
                            if (!raw) continue;
                            const key = toActivityKey(raw);
                            const tr = t(key);
                            if (tr !== key) return tr;
                          }
                          return event.activity || event.status || "—";
                        })()}
                      </span>
                    </span>
                  </div>
                ))
              ) : (
                <div className={styles.events_empty}>
                  {t("lbl_no_events_yet")}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShippingDetails;
